/**
 * Share / don't-share for one review item. Unshared shows both icons.
 * Picking one collapses to that icon. Clicking it again clears the choice.
 */
import { CheckOutlined, StopOutlined } from "@ant-design/icons"
import { Button, Popconfirm, Tooltip } from "antd"
import { useState } from "react"

import { useThemeContext } from "#contexts/ThemeContextProvider"

export type ShareChoiceState = "open" | "shared" | "blocked"

const SHARE_CONFIRM =
  "This looks similar to an earlier comment for this kid. Share anyway?"

const iconButtonStyle = (size: number, iconSize: number) =>
  ({
    width: size,
    height: size,
    minWidth: size,
    padding: 0,
    fontSize: iconSize,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }) as const

export const ShareChoice = ({
  state,
  onShare,
  onUnshare,
  onDontShare,
  onAllow,
  loading = false,
  shareDisabled = false,
  confirmShare = false,
  shareTestId = "sr-share-switch",
  blockTestId = "sr-dont-share-message",
  buttonSize = 28,
  iconSize,
  momentId,
  artworkId,
  storyTimeId,
}: {
  state: ShareChoiceState
  onShare: () => void
  onUnshare: () => void
  /** Omitted when this item cannot be marked don't-share (Storytime). */
  onDontShare?: () => void
  onAllow?: () => void
  loading?: boolean
  shareDisabled?: boolean
  /** Ask before sharing a note that looks like an earlier one. */
  confirmShare?: boolean
  shareTestId?: string
  blockTestId?: string
  /** Edge length of each icon button. Review uses 28; Quality Check uses 30. */
  buttonSize?: number
  /** Glyph size inside the button. Review leaves this at 14 so the icon has padding. */
  iconSize?: number
  momentId?: string
  artworkId?: string
  storyTimeId?: string
}) => {
  const theme = useThemeContext()
  const [confirming, setConfirming] = useState(false)
  const shared = state === "shared"
  const blocked = state === "blocked"
  const shareTip = shared ? "Shared. Click to unshare." : "Share"
  const blockTip = blocked
    ? "Don't share. Click to allow share."
    : "Don't share"
  const ask = confirmShare && state === "open"
  const iconButton = iconButtonStyle(buttonSize, iconSize ?? 14)

  const shareButton = (
    <Tooltip title={shareTip}>
      <Button
        data-testid={shareTestId}
        data-state={state}
        data-shared={shared ? "true" : "false"}
        data-moment-id={momentId}
        data-artwork-id={artworkId}
        data-story-time-id={storyTimeId}
        size="small"
        role="switch"
        aria-checked={shared}
        aria-label={shareTip}
        disabled={shareDisabled || loading}
        icon={<CheckOutlined />}
        onClick={() => {
          if (shared) {
            onUnshare()
            return
          }
          if (ask) {
            setConfirming(true)
            return
          }
          onShare()
        }}
        style={{
          ...iconButton,
          ...(shared
            ? {
                color: "#fff",
                backgroundColor: theme.eric.green,
                borderColor: theme.eric.green,
              }
            : { color: theme.eric.green, borderColor: theme.eric.green }),
        }}
      />
    </Tooltip>
  )

  const blockButton =
    onDontShare != null ? (
      <Tooltip title={blockTip}>
        <Button
          data-testid={blockTestId}
          data-state={state}
          data-moment-id={momentId}
          data-artwork-id={artworkId}
          size="small"
          aria-label={blockTip}
          disabled={loading}
          icon={<StopOutlined />}
          onClick={() => {
            if (blocked) {
              onAllow?.()
              return
            }
            onDontShare()
          }}
          style={{
            ...iconButton,
            ...(blocked
              ? {
                  color: "#fff",
                  backgroundColor: theme.eric.red,
                  borderColor: theme.eric.red,
                }
              : { color: theme.eric.red, borderColor: theme.eric.red }),
          }}
        />
      </Tooltip>
    ) : null

  return (
    <div
      data-testid="sr-share-choice"
      style={{
        display: "flex",
        flex: "0 0 auto",
        alignItems: "center",
        alignSelf: "center",
        gap: 4,
      }}
    >
      {blocked ? null : ask ? (
        <Popconfirm
          open={confirming}
          onOpenChange={(next) => {
            if (!next) setConfirming(false)
          }}
          title={SHARE_CONFIRM}
          okText="Share"
          onConfirm={() => {
            setConfirming(false)
            onShare()
          }}
          onCancel={() => setConfirming(false)}
        >
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {shareButton}
          </span>
        </Popconfirm>
      ) : (
        shareButton
      )}
      {shared ? null : blockButton}
    </div>
  )
}
